import { ChevronLeft } from 'lucide-react';
import type { CandidateInfoConfig } from '@repo/ui/components/custom/candidate/info/component';
import { useNavigate } from 'react-router';

import {
  useOpenCallUiConfig,
  type SubmissionWithTypedPayload,
} from '@/open-call-types';
import { CandidateAndReview } from '@/state/use-candidate';
import * as permifyConstants from '@repo/api/permify/constants';
import { ConfigService } from '@/services';
import { RatingAverageResponse } from '@repo/api/open-calls/graphql';
import {
  CandidateIdentityCard,
  CandidateInfo,
  CandidatePortfolio,
  DismissCandidate,
  MoveCandidateToStage,
  SpotlightMediaNavigationControlsPosition,
} from '@repo/ui/components/custom';
import { Button } from '@repo/ui/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@repo/ui/components/ui/tabs';
import { useApplyViewPermissions, useScreenSize } from '@repo/ui/hooks';
import { Review } from '@repo/ui/models/candidate';
import { useViewPermissions } from '@repo/ui/state';
import { ReviewCandidate } from './candidate.review';
import { CandidateAllScores, ReplyPayload } from './candidate.scores';
import { SelfCommentModel } from './self-comment';

type CandidateProps = {
  candidate: CandidateAndReview;
  ratingAverage?: RatingAverageResponse | null;
  processing?: boolean;
  rating?: number;
  onCommentReplyClick: (data: ReplyPayload) => void;
  onCommentAddClick: (rating: number, comment?: string) => void;
  onCommentEditClick: (review: Review) => void;
  onSkipClick: (rating: number) => void;
  onUpdateClick: (commentId: string, comment: string) => void;
  userName: string;
  userComment?: SelfCommentModel;
  inEditMode?: boolean;
};

export function Candidate(props: CandidateProps) {
  const navigate = useNavigate();
  const { data: permissions, isAdmin } = useViewPermissions();
  const typedCandidate = props.candidate as SubmissionWithTypedPayload;
  const { getCandidateDisplay, uiConfig: openCallUiConfig } =
    useOpenCallUiConfig();
  const candidateDisplay = getCandidateDisplay(typedCandidate);

  useApplyViewPermissions(permissions);

  const viewCandidateInfoPermission =
    permifyConstants.permissions.openCalls.viewCandidateInfo;

  return (
    <div className="flex gap-4 h-full w-full">
      <div className="flex flex-col gap-2 w-full h-full">
        <div className="flex items-center justify-between sm:justify-end p-2 sm:border-none border-b border-zinc-200 bg-white sm:bg-transparent">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ChevronLeft className="h-4 w-4 sm:hidden" />
          </Button>
          <div className="flex gap-2">
            <DismissCandidate
              candidateIds={[props.candidate._id]}
              className="text-red-600 hover:text-red-500 hover:bg-transparent"
            />
            <MoveCandidateToStage candidateIds={[props.candidate._id]} />
          </div>
        </div>
        <div className="grid gap-4">
          <CandidateIdentityCard
            candidate={props.candidate}
            title={candidateDisplay.title}
          />
        </div>

        <ReviewCandidate
          onCommentAddClick={props.onCommentAddClick}
          onSkipClick={props.onSkipClick}
          onUpdateClick={props.onUpdateClick}
          processing={props.processing}
          rating={props.rating}
          userName={props.userName}
          userComment={props.userComment}
          editComment={props.inEditMode}
        />

        <div
          className="overflow-y-auto flex-1"
          data-permission={viewCandidateInfoPermission}
        >
          <CandidateSections
            candidate={props.candidate}
            onCommentAddClick={props.onCommentAddClick}
            onSkipClick={props.onSkipClick}
            onUpdateClick={props.onUpdateClick}
            rating={props.rating}
            ratingAverage={props.ratingAverage}
            processing={props.processing}
            onCommentEditClick={props.onCommentEditClick}
            userName={props.userName}
            onCommentReplyClick={props.onCommentReplyClick}
            candidateInfoConfig={openCallUiConfig.candidateDetails}
          />
        </div>
        <div
          className="overflow-y-auto flex-1 flex-grow-1"
          data-permission={viewCandidateInfoPermission}
          data-permission-action-override={isAdmin() ? 'hide' : 'show'}
        >
          <CandidateInfo
            candidate={props.candidate}
            detailsConfig={openCallUiConfig.candidateDetails}
          />
        </div>
      </div>
      <div className="flex-col hidden lg:flex min-w-96 xl:min-w-114 flex-grow-2 h-full w-full">
        <CandidatePortfolio
          key={props.candidate._id}
          candidate={props.candidate}
          spotlightMediaNavigationControlsPosition={
            SpotlightMediaNavigationControlsPosition.center
          }
          mediaAPIURL={ConfigService.PROXY_URL}
        />
      </div>
    </div>
  );
}

function CandidateSections(
  props: CandidateProps & {
    candidateInfoConfig?: CandidateInfoConfig;
    onCommentEditClick: (review: Review) => void;
  },
) {
  const { isSmall, isMedium } = useScreenSize();
  const defaultTab = isSmall || isMedium ? 'portfolio' : 'info';

  return (
    <Tabs className="w-full pb-14" defaultValue={defaultTab}>
      <TabsList className="w-full">
        <TabsTrigger className="w-full lg:hidden" value="portfolio">
          Portfolio
        </TabsTrigger>
        <TabsTrigger className="w-full" value="info">
          Info
        </TabsTrigger>
        <TabsTrigger className="w-full" value="reviews">
          All Scores
        </TabsTrigger>
      </TabsList>
      <TabsContent className="lg:hidden" value="portfolio">
        <CandidatePortfolio
          candidate={props.candidate}
          mediaAPIURL={ConfigService.PROXY_URL}
        />
      </TabsContent>
      <TabsContent value="info">
        <CandidateInfo
          candidate={props.candidate}
          detailsConfig={props.candidateInfoConfig}
        />
      </TabsContent>
      <TabsContent value="reviews">
        <CandidateAllScores
          ratingAverage={props.ratingAverage}
          reviews={props.candidate.reviews}
          processing={props.processing}
          submissionId={props.candidate._id}
          userName={props.userName ?? ''}
          onCommentEditClick={props.onCommentEditClick}
          onReplayClick={props.onCommentReplyClick}
          onUpdateClick={props.onUpdateClick}
        />
      </TabsContent>
    </Tabs>
  );
}
